import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { Session } from 'talkjs/all';
import useLoggedInUser from '@rsos/hooks/useLoggedInUser';
import useHandleUnreadConversations from '@rsos/reduxDataStore/src/features/conversations/useHandleUnreadConversations';
import useHandleUnreadCollaborator from '@rsos/reduxDataStore/src/features/emergencyCalls/useHandleUnreadCollaborator';
import useHandleUnreadRCS from '@rsos/reduxDataStore/src/features/rcs/useHandleUnreadRCS';
import { filteredAlertsSelector } from '@rsos/reduxDataStore/src/features/selectors/alertsSelectors';
import { callQueueSelector } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import useHandleTF911UnreadMessages from '@rsos/reduxDataStore/src/features/textFrom911/useHandleTF911UnreadMessages';
import checkAniAliAccessSelector from '@rsos/utils/checkAniAliAccessSelector';
import { handleUnreadAlerts } from '@rsos/utils/handleReadUnreadAlerts';
import { captureException } from '@rsos/utils/sentry';
import { createTalkSession } from '@rsos/utils/talkJS';
import { RootState } from '../app';
import { handleUnreadCalls } from '../utils/handleReadUnreadCalls';

/**
 * Track unread alerts and calls via TalkJS when no alert or call is selected,
 * or when a call is selected but not shared or received via agency share/chat.
 */
const useTrackUnreads = () => {
  const dispatch = useDispatch();
  const hasAniAliAccess = useSelector((state: RootState) =>
    checkAniAliAccessSelector({ psaps: state.psaps, sinatra: state.sinatra }),
  );

  const filteredCalls = useSelector((state: RootState) =>
    callQueueSelector(state.emergencyCalls.callQueue, hasAniAliAccess),
  );

  const selectedCallerID = useSelector(
    (state: RootState) => state.emergencyCalls.selectedCallerID,
  );

  const isCallReceived = selectedCallerID
    ? filteredCalls?.[selectedCallerID]?.agencyShareChat?.isCallReceived
    : false;

  const isCallShared = selectedCallerID
    ? filteredCalls?.[selectedCallerID]?.agencyShareChat?.isCallShared
    : false;

  const alertsQueue = useSelector((state: RootState) =>
    filteredAlertsSelector(state.incidents),
  );

  const selectedAlertID = useSelector(
    (state: RootState) => state.incidents.selectedAlertID,
  );

  const latestMsgTime = useSelector(
    (state: RootState) => state.incidents.latestMsgTime,
  );

  const loggedInUser = useLoggedInUser('rsp_calls_user');

  const [chatSession, setChatSession] = useState<Session | null>(null);

  useHandleUnreadConversations(chatSession);
  useHandleUnreadCollaborator(chatSession);
  useHandleUnreadRCS(chatSession);
  useHandleTF911UnreadMessages(chatSession);

  useEffect(() => {
    let session;

    try {
      (async () => {
        // No role is set here as the purpose of this hook is to just track
        // unread messages for both calls and alerts.
        session = await createTalkSession(loggedInUser);

        setChatSession(session);
      })();
    } catch (error) {
      captureException(error);
    }
  }, [loggedInUser]);

  useEffect(() => {
    // All events view
    const noCallOrAlertSelected = !selectedAlertID && !selectedCallerID;

    // When a call is selected but not shared or received
    const selectedCallNotReceivedOrShared =
      selectedCallerID && !isCallReceived && !isCallShared;

    if (
      chatSession &&
      (noCallOrAlertSelected || selectedCallNotReceivedOrShared)
    ) {
      if (Object.keys(filteredCalls).length) {
        handleUnreadCalls(
          chatSession,
          filteredCalls,
          selectedCallerID,
          dispatch,
        );
      }

      if (Object.keys(alertsQueue).length) {
        handleUnreadAlerts(
          chatSession,
          alertsQueue,
          selectedAlertID,
          latestMsgTime,
          dispatch,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chatSession,
    isCallReceived,
    isCallShared,
    selectedAlertID,
    selectedCallerID,
  ]);

  return chatSession;
};

export default useTrackUnreads;
